/** A semana da Agenda no computador (docs/design: grade de 7 dias). Uma leitura do banco para os 7 dias. */
import { includesService } from '../domain/appointment-services';
import { conflictIds, findOverlaps } from '../domain/appointments';
import { openRangesForDay } from '../domain/availability';
import { cashSummary } from '../domain/cash';
import { packageBalance } from '../domain/packages';
import { type LocalDate, addDays, dayBoundsUtc, todayIn, toIso, toMs } from '../domain/time';
import type { Appointment } from '../schemas/entities';
import { type AgendaItem, type DayAgenda, useLiveQuery } from './index';

export interface WeekDay extends DayAgenda {
  /** recebido no dia (pagamentos com data desse dia) */
  receivedCents: number;
}

/** Os 7 dias a partir de `from` (normalmente o domingo da semana). */
export function useWeekAgenda(from: LocalDate): WeekDay[] | undefined {
  return useLiveQuery(
    async (rt) => {
      const prof = await rt.db.get('professional', rt.db.professionalId());
      if (!prof) return undefined;
      const tz = prof.timezone;
      const dates = Array.from({ length: 7 }, (_, i) => addDays(from, i));
      const first = dayBoundsUtc(dates[0]!, tz).start;
      const last = dayBoundsUtc(dates[6]!, tz).end;
      const [appts, blocks, rules, windows, clients, services, packages, allAppts, payments, appointmentServices] = await Promise.all([
        rt.db.range('appointment', 'start_utc', toIso(first - 24 * 3_600_000), toIso(last)),
        rt.db.all('block'),
        rt.db.all('availability_rule'),
        rt.db.range('availability_window', 'date', dates[0]!, dates[6]!),
        rt.db.all('client'),
        rt.db.all('service'),
        rt.db.all('package'),
        rt.db.all('appointment'),
        rt.db.all('payment'),
        rt.db.all('appointment_service'),
      ]);
      const clientById = new Map(clients.map((c) => [c.id, c]));
      const serviceById = new Map(services.map((s) => [s.id, s]));
      const today = todayIn(tz);
      return dates.map((date): WeekDay => {
        const { start, end } = dayBoundsUtc(date, tz);
        const ofDay = appts.filter((a) => toMs(a.start_utc) < end && toMs(a.end_utc) > start);
        const conflicted = conflictIds(ofDay);
        const toItem = (a: Appointment): AgendaItem => {
          // O serviço do pacote pode ser o segundo do atendimento.
          const pkgs = packages.filter((p) => p.client_id === a.client_id && includesService(a, p.service_id, appointmentServices));
          const pkg = a.package_id ? packages.find((p) => p.id === a.package_id) : pkgs[0];
          const balance = pkg ? packageBalance(pkg, allAppts, today) : null;
          return {
            appointment: a,
            client: clientById.get(a.client_id),
            service: serviceById.get(a.service_id),
            conflict: conflicted.has(a.id),
            packageBalance: balance ? { remaining: balance.remaining, total: balance.total } : null,
          };
        };
        const sorted = ofDay.sort((a, b) => toMs(a.start_utc) - toMs(b.start_utc));
        const items = sorted.filter((a) => a.status !== 'canceled' && a.status !== 'no_show').map(toItem);
        const itemById = new Map(items.map((i) => [i.appointment.id, i]));
        return {
          date,
          timezone: tz,
          items,
          inactive: sorted.filter((a) => a.status === 'canceled' || a.status === 'no_show').map(toItem),
          blocks: blocks.filter((b) => toMs(b.start_utc) < end && toMs(b.end_utc) > start).sort((a, b) => toMs(a.start_utc) - toMs(b.start_utc)),
          openRanges: openRangesForDay(
            date,
            prof,
            rules,
            windows.filter((w) => w.date === date),
          ),
          overlaps: findOverlaps(ofDay)
            .map(([a, b]) => [itemById.get(a.id), itemById.get(b.id)] as const)
            .filter((p): p is readonly [AgendaItem, AgendaItem] => Boolean(p[0] && p[1]))
            .map((p) => [p[0], p[1]] as [AgendaItem, AgendaItem]),
          receivedCents: cashSummary(payments, date, date, tz).total,
        };
      });
    },
    [from],
    ['appointment', 'block', 'availability_rule', 'availability_window', 'client', 'service', 'package', 'payment', 'professional', 'appointment_service'],
  );
}
